import { Box, Container, Modal, Stack, Typography } from "@mui/material";
import { useState } from "react";

import { renderErrorOrLoading } from "../commons/renderErrorOrLoading";
import { LoginIndicator } from "../components/LoginIndicator";
import { MatchupView } from "../components/MatchupView";
import { Standings } from "../components/Standings";
import { appConfig } from "../config";
import type { MatchupRow } from "../data/types";
import { useData } from "../data/useData";
import { useUser } from "../data/useUser";
import { ResultsDialog } from "./ResultsDialog";
import { UserDialog } from "./UserDialog";

const GUEST_USER = "GUEST";

export function Main(): JSX.Element {
  const { matchups = [], rows, loading, error } = useData();
  const { user, setUser } = useUser();
  const [selectedMatchup, setSelectedMatchup] = useState<MatchupRow>();
  const errorOrLoading = renderErrorOrLoading({ loading, error });

  if (errorOrLoading) return errorOrLoading;

  return (
    <Container component="main" maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
      <LoginIndicator />
      <Stack spacing={{ xs: 3, sm: 4 }}>
        <Box>
          <Typography variant="h4" component="h1">
            {appConfig.tournamentName} Results
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            Standings and matchup results
          </Typography>
        </Box>
        <Standings />
        <Box
          aria-label="matchups"
          component="section"
          sx={{
            alignItems: "start",
            display: "grid",
            gap: { xs: 2.5, sm: 3 },
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
            },
            width: "100%",
          }}
        >
          {matchups.map((matchup, index) => (
            <MatchupView
              key={matchup.index ?? index}
              {...matchup}
              index={matchup.index ?? index}
              onOpen={() => setSelectedMatchup(matchup)}
            />
          ))}
        </Box>
      </Stack>
      <Modal open={!user} onClose={() => setUser(GUEST_USER)}>
        <UserDialog rows={rows} onSetUser={setUser} />
      </Modal>
      {selectedMatchup && (
        <Modal open onClose={() => setSelectedMatchup(undefined)}>
          <ResultsDialog
            matchup={selectedMatchup}
            onClose={() => setSelectedMatchup(undefined)}
          />
        </Modal>
      )}
    </Container>
  );
}
